import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { FiArrowLeft, FiCpu, FiDollarSign, FiActivity, FiClock, FiBarChart2, FiZap } from 'react-icons/fi';
import { MdCheckCircle, MdPerson, MdWork } from 'react-icons/md';
import Layout from '../components/Layout';

// GaugeChart Component (inline for admin-panel)
interface GaugeChartProps {
  score: number;
  size?: 'sm' | 'md';
  showLabel?: boolean;
}

const GaugeChart: React.FC<GaugeChartProps> = ({ score, size = 'sm', showLabel = true }) => {
  const normalizedScore = Math.max(0, Math.min(100, score));
  
  const dimensions = {
    sm: { width: 80, height: 50, strokeWidth: 6, fontSize: '14px', labelSize: '10px' },
    md: { width: 100, height: 60, strokeWidth: 8, fontSize: '16px', labelSize: '11px' },
  };
  
  const { width, height, strokeWidth, fontSize, labelSize } = dimensions[size];
  const radius = (width / 2) - (strokeWidth / 2) - 5;
  const circumference = Math.PI * radius;
  
  const progress = (normalizedScore / 100) * circumference;
  const offset = circumference - progress;
  
  const config = { 
    color: '#3B82F6', 
    label: normalizedScore >= 91 ? 'Exceeded' : normalizedScore >= 76 ? 'Very Good' : normalizedScore >= 61 ? 'Good' : normalizedScore >= 36 ? 'Fair' : 'Poor',
    gradient: 'url(#gradient-blue)' 
  };
  
  const centerX = width / 2;
  const centerY = height - 5;
  
  return (
    <div className="flex flex-col items-center">
      <svg width={width} height={height} className="overflow-visible">
        <defs>
          <linearGradient id="gradient-blue" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
        </defs>
        
        <path
          d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className="dark:stroke-gray-700"
        />
        
        <path
          d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`}
          fill="none"
          stroke={config.gradient}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition: 'stroke-dashoffset 0.5s ease',
            transformOrigin: 'center',
          }}
        />
        
        <text
          x={centerX}
          y={centerY - 8}
          textAnchor="middle"
          className="font-bold fill-navy-700 dark:fill-white"
          style={{ fontSize }}
        >
          {Math.round(normalizedScore)}%
        </text>
      </svg>
      
      {showLabel && (
        <span 
          className="mt-1 font-semibold"
          style={{ 
            color: config.color, 
            fontSize: labelSize 
          }}
        >
          {config.label}
        </span>
      )}
    </div>
  );
};

interface CVUpload {
  id: number;
  filename_original: string;
  candidate_name: string;
  score: string | null;
}

interface UsageHistoryItem {
  id: number;
  cv_upload: CVUpload | null;
  action_type: string;
  cv_count: number;
  tokens_used: number;
  input_tokens: number;
  output_tokens: number;
  processing_time_ms: number | null;
  cost_usd: string | null;
  status: string;
  created_at: string;
}

interface Stats {
  total_tokens: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: string;
  avg_processing_ms: number;
  total_api_calls: number;
}

interface AnalysisRunDetail {
  id: number;
  run_id: string;
  run_type: 'cv_analysis' | 'interview_tips';
  job: {
    id: number;
    title: string;
    description: string;
    status: string;
    skills: any[];
    created_at: string;
  } | null;
  user: {
    id: number;
    full_name: string;
    email: string;
    company: string;
    phone: string;
    country: string;
    is_active: boolean;
    package: {
      id: number;
      title: string;
    } | null;
  } | null;
  status: string;
  total_cvs: number;
  processed_cvs: number;
  failed_cvs: number;
  progress: number;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number | null;
  error_message: string;
  created_at: string;
  usage_history: UsageHistoryItem[];
  stats: Stats;
}

const UsageDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [analysisRun, setAnalysisRun] = useState<AnalysisRunDetail | null>(null);

  useEffect(() => {
    if (id) {
      fetchAnalysisRunDetail(parseInt(id));
    }
  }, [id]);

  const fetchAnalysisRunDetail = async (runId: number) => {
    try {
      setLoading(true);
      const response = await apiService.getAnalysisRunDetail(runId);

      if (response.success && response.data) {
        setAnalysisRun(response.data);
      }
    } catch (error) {
      console.error('Error fetching analysis run detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Undefined';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'Undefined';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getStatusBadge = (status: string) => {
    const configs: Record<string, { color: string; bg: string; text: string }> = {
      completed: { color: 'text-green-600', bg: 'bg-green-100', text: 'Completed' },
      running: { color: 'text-blue-600', bg: 'bg-blue-100', text: 'Running' },
      failed: { color: 'text-red-600', bg: 'bg-red-100', text: 'Failed' },
      pending: { color: 'text-yellow-600', bg: 'bg-yellow-100', text: 'Pending' },
    };

    const config = configs[status] || configs.pending;

    return (
      <span className={`inline-flex items-center gap-1 rounded-full ${config.bg} px-3 py-1 text-sm font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getRunTypeBadge = (runType: string) => {
    const configs: Record<string, { icon: any; color: string; bg: string; text: string }> = {
      cv_analysis: { icon: FiBarChart2, color: 'text-blue-600', bg: 'bg-blue-100', text: 'CV Analysis' },
      interview_tips: { icon: FiZap, color: 'text-purple-600', bg: 'bg-purple-100', text: 'Interview Tips' },
    };

    const config = configs[runType] || configs.cv_analysis;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 rounded-full ${config.bg} px-3 py-1 text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-navy-900">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!analysisRun) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-navy-900">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400">Analysis run not found</p>
            <button
              onClick={() => navigate('/usage')}
              className="mt-4 text-brand-500 hover:underline"
            >
              Back to Usage
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-navy-900">
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/usage')}
          className="mb-3 flex items-center gap-2 text-brand-500 transition hover:text-brand-600"
        >
          <FiArrowLeft className="h-5 w-5" />
          Back to Usage
        </button>
        <div className="flex items-center justify-between">
          <div>
           
            <p className="mt-1 font-mono text-sm text-gray-600 dark:text-gray-400">
              Run ID: {analysisRun.run_id}
            </p>
            {analysisRun.job && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Job: <span className="font-medium text-navy-700 dark:text-white">{analysisRun.job.title}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {getRunTypeBadge(analysisRun.run_type)}
            {getStatusBadge(analysisRun.status)}
          </div>
        </div>
      </div>

      {/* User Info */}
      {analysisRun.user && (
        <div className="mb-4 rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <MdPerson className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-base font-semibold text-navy-700 dark:text-white">User Information</h3>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Name</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">{analysisRun.user.full_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Email</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">{analysisRun.user.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Company</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">{analysisRun.user.company}</p>
            </div>
            {analysisRun.user.package && (
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Package</p>
                <span className="inline-block rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                  {analysisRun.user.package.title}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {/* Total CVs */}
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <FiActivity className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total CVs</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {analysisRun.total_cvs}
              </h3>
            </div>
          </div>
        </div>

        {/* Processed */}
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <MdCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Processed</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {analysisRun.processed_cvs}
              </h3>
            </div>
          </div>
        </div>

        {/* Total Tokens */}
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
              <FiCpu className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tokens</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {((analysisRun.stats.total_input_tokens + analysisRun.stats.total_output_tokens) / 1000).toFixed(1)}K
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                ↑{(analysisRun.stats.total_input_tokens / 1000).toFixed(1)}K ↓{(analysisRun.stats.total_output_tokens / 1000).toFixed(1)}K
              </p>
            </div>
          </div>
        </div>

        {/* Total Cost */}
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <FiDollarSign className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Cost</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                ${Number(parseFloat(analysisRun.stats.total_cost_usd).toFixed(4))}
              </h3>
              {analysisRun.processed_cvs > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  ${Number((parseFloat(analysisRun.stats.total_cost_usd) / analysisRun.processed_cvs).toFixed(6))}/CV
                </p>
              )}
            </div>
          </div>
        </div>

        {/* API Calls */}
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
              <FiActivity className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">API Calls</p>
              <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                {analysisRun.stats.total_api_calls}
              </h3>
            </div>
          </div>
        </div>

        {/* Avg Processing */}
        <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
              <FiClock className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Time</p>
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                {(analysisRun.stats.avg_processing_ms / 1000).toFixed(1)}s
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Timestamps */}
      <div className="mb-4 rounded-xl bg-white p-4 shadow-sm dark:bg-navy-800">
        <h3 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Timeline</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Created At</p>
            <p className="font-medium text-navy-700 dark:text-white">{formatDate(analysisRun.created_at)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Started At</p>
            <p className="font-medium text-navy-700 dark:text-white">{formatDate(analysisRun.started_at)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Completed At</p>
            <p className="font-medium text-navy-700 dark:text-white">{formatDate(analysisRun.completed_at)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Duration</p>
            <p className="font-medium text-navy-700 dark:text-white">{formatDuration(analysisRun.duration_seconds)}</p>
          </div>
        </div>
      </div>

      {/* Usage History Table */}
      <div className="rounded-xl bg-white shadow-sm dark:bg-navy-800">
        <div className="border-b border-gray-200 p-4 dark:border-white/10">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
            Usage History ({analysisRun.usage_history.length} CVs)
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-navy-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  CV
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Candidate
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Score
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Tokens
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300">
                  Analyzed At
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/10">
              {analysisRun.usage_history.map((usage) => (
                <tr
                  key={usage.id}
                  className="transition hover:bg-gray-50 dark:hover:bg-navy-900/50"
                >
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900 dark:text-white">
                      {usage.cv_upload?.filename_original || 'Undefined'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {usage.cv_upload?.candidate_name || 'Undefined'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center">
                    {(() => {
                      const score = usage.cv_upload?.score;
                      
                      // FIRST: Check if score is explicitly null, undefined, or empty
                      if (score === null || score === undefined || score === '') {
                        return <span className="text-sm text-gray-500 dark:text-gray-400">Undefined</span>;
                      }
                      
                      // SECOND: Try to parse as number (handles "0", "50", etc.)
                      const numScore = parseFloat(String(score));
                      
                      // THIRD: Check if parsing resulted in a valid number
                      if (isNaN(numScore)) {
                        return <span className="text-sm text-gray-500 dark:text-gray-400">Undefined</span>;
                      }
                      
                      // FINALLY: Show gauge chart for ALL valid numbers including 0
                      return <GaugeChart score={numScore} size="sm" showLabel={false} />;
                    })()}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {((usage.input_tokens + usage.output_tokens) / 1000).toFixed(1)}K
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ↑{(usage.input_tokens / 1000).toFixed(1)}K ↓{(usage.output_tokens / 1000).toFixed(1)}K
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center">
                    <span className="text-sm text-gray-900 dark:text-white">
                      ${usage.cost_usd ? Number(parseFloat(usage.cost_usd).toFixed(6)) : '0.00'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(usage.created_at)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {analysisRun.usage_history.length === 0 && (
          <div className="p-12 text-center">
            <p className="text-gray-500 dark:text-gray-400">No usage history found for this run</p>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default UsageDetail;
